import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { DispoService } from '../dispos/dispo.service';
import { CallerReportService } from '../caller-report/caller-report.service';

/**
 * Dispo-type buckets — verified against dispo-reports.component.ts:155-158 so Campaign
 * Effort reconciles with the Dispos/Callers reports' color buckets.
 *
 * NOTE: `FIX` (rework / replacement) is included in the GREEN/lead set to match the reports'
 * green bucket exactly (so our "Leads" reconciles with the Callers Report "Lead" column).
 * A disposition is a LEAD iff `dispo ∈ GREEN_LEAD`. HOT is its own (yellow) bucket — never a lead.
 */
export const GREEN_LEAD = ['FACE', 'PHONE', 'ZOOM', 'FIX'];
export const YELLOW_HOT = ['HOT', 'FYI', 'SINFO'];
export const BLUE_NOANS = ['NOMSG', 'MSG', 'NOANS', 'TREE', 'GKINFO'];
export const BLACK_BAD = ['FAX', 'BUSY', 'DISCO', 'NOTINT', 'WRONG', 'DONOTCALL', 'NOMARKET'];

/** One row from `dispos/get_dispos_report`. */
export interface DispoRow {
	id: number | string;
	prospect_name: string;
	primary_phone: string;
	dispo: string;
	caller_name: string;
	campaign_name: string;
	date: string;
	notes: string;
}

export interface TalkRow {
	campaign_id: number | string;
	campaign_name: string;
	talk_seconds: number | string;
	calls: number | string;
}

/** One row from `call_report/get_all_caller_times`. */
export interface CallerTimeRow {
	name: string;
	timeInCalls: number | string;
	totalCalls: number | string;
	totalDispos: number | string;
	black: string;
	blue: string;
	yellow: string;
	green: string;
	other: string;
}

export interface CampaignEffortRow {
	campaign: string;
	worked: number; // prospects worked = count of dispo rows
	leads: number;
	noAnswer: number; // blue
	callback: number; // yellow
	bad: number; // black
	talkMs: number; // estimated — allocated from callers_calls
	workedPerLead: number;
	effortScore: number;
	calls: number;
}

export interface CallerEffortRow {
	name: string;
	dispos: number;
	calls: number; // RingCentral — estimated
	hot: number; // yellow
	leads: number; // green
	talkMs: number; // RingCentral — estimated
	prospectsPerLead: number;
	conv: number; // leads / dispos, %
	topCampaign: string;
	callerMetricsUnavailable?: boolean; // true when built from dispos only (caller-times endpoint failed)
}

export interface EffortRangeData {
	dispos: DispoRow[];
	callerTimes: CallerTimeRow[];
	talk: TalkRow[];
	disposFailed: boolean; // the dispos/get_dispos_report call errored
	callerTimesFailed: boolean; // the call_report/get_all_caller_times call errored
	talkFailed: boolean;
}

/**
 * CampaignEffortService — composes the EXISTING dispo + caller-report services (no backend
 * changes, no new endpoints) and does the effort aggregation in memory. All data flows
 * through `environment.api` via those services, so prod builds hit prod automatically.
 *
 * Additive: this service only CALLS `DispoService` / `CallerReportService`; it never edits them.
 */
@Injectable({ providedIn: 'root' })
export class CampaignEffortService {
	constructor(
		private dispoSrv: DispoService,
		private callerSrv: CallerReportService,
	) {}

	/**
	 * Fetch once per date range: all dispos (every type) + per-caller times. Aggregate in memory.
	 * Fault-tolerant: each call is wrapped in catchError so one failing endpoint yields an empty
	 * result ({body: []}) with a `__failed` marker instead of aborting BOTH — the component then
	 * renders whatever survived. Payloads are unchanged.
	 */
	loadRange(from: string, to: string): Observable<EffortRangeData> {
		return forkJoin({
			dispos: this.dispoSrv.getDisposReport(from, to, 'all').pipe(
				catchError((err: any) => {
					// TEMP diagnostic — identifies which endpoint 500s. Remove once confirmed.
					console.error(`[CampaignEffort] dispos endpoint failed — HTTP ${err?.status ?? '??'} POST dispos/get_dispos_report`, err);
					return of({ body: [], __failed: true });
				}),
			),
			// callerTimes: this.callerSrv.get_all_callers_times(from, to).pipe(
			// 	catchError((err: any) => {
			// 		// TEMP diagnostic — identifies which endpoint 500s. Remove once confirmed.
			// 		console.error(
			// 			`[CampaignEffort] callerTimes endpoint failed — HTTP ${err?.status ?? '??'} POST call_report/get_all_caller_times`,
			// 			err,
			// 		);
			// 		return of({ body: [], __failed: true });
			// 	}),
			// ),
			talk: this.callerSrv.getTalkByCampaign(from, to).pipe(
				catchError((err: any) => {
					console.error(`[CampaignEffort] talk endpoint failed — HTTP ${err?.status ?? '??'} POST call_report/get_talk_by_campaign`, err);
					return of({ body: [], __failed: true });
				}),
			),
		}).pipe(
			map((res: any) => ({
				dispos: (res.dispos?.body ?? []) as DispoRow[],
				callerTimes: (res.callerTimes?.body ?? []) as CallerTimeRow[],
				talk: (res.talk?.body ?? []) as TalkRow[],
				disposFailed: !!res.dispos?.__failed,
				callerTimesFailed: !!res.callerTimes?.__failed,
				talkFailed: !!res.talk?.__failed,
			})),
		);
	}

	/** Lead detail for the receipt — reuse the existing dispo-details endpoint. */
	getDispoDetails(dispoId: number | string) {
		return this.dispoSrv.getDispoDetails(dispoId);
	}

	isLead(dispo: string): boolean {
		return GREEN_LEAD.includes(dispo);
	}

	bucketOf(dispo: string): 'green' | 'yellow' | 'blue' | 'black' | 'other' {
		if (GREEN_LEAD.includes(dispo)) return 'green';
		if (YELLOW_HOT.includes(dispo)) return 'yellow';
		if (BLUE_NOANS.includes(dispo)) return 'blue';
		if (BLACK_BAD.includes(dispo)) return 'black';
		return 'other';
	}

	// ── By Campaign ──────────────────────────────────────────────────────────
	byCampaign(dispos: DispoRow[], callerTimes: CallerTimeRow[], talkRows: TalkRow[]): CampaignEffortRow[] {
		const talk = new Map<string, number>();
		const calls = new Map<string, number>();
		for (const t of talkRows) {
			talk.set(t.campaign_name, this.n(t.talk_seconds) * 1000);
			calls.set(t.campaign_name, this.n(t.calls));
		}
		const groups = new Map<string, DispoRow[]>();
		for (const d of dispos) {
			const g = groups.get(d.campaign_name) ?? [];
			g.push(d);
			groups.set(d.campaign_name, g);
		}
		const rows: CampaignEffortRow[] = [];
		groups.forEach((list, campaign) => {
			const worked = list.length;
			const leads = list.filter((d) => this.isLead(d.dispo)).length;
			const noAnswer = list.filter((d) => this.bucketOf(d.dispo) === 'blue').length;
			const callback = list.filter((d) => this.bucketOf(d.dispo) === 'yellow').length;
			const bad = list.filter((d) => this.bucketOf(d.dispo) === 'black').length;
			const wpl = leads ? worked / leads : 0;
			rows.push({
				campaign: campaign || '—',
				worked,
				leads,
				noAnswer,
				callback,
				bad,
				talkMs: talk.get(campaign) ?? 0,
				calls: calls.get(campaign) ?? 0,
				workedPerLead: wpl,
				effortScore: this.effortScore(wpl),
			});
		});
		return rows.sort((a, b) => b.leads - a.leads);
	}

	/** Green (lead) dispos delivered for a campaign — the drill list. */
	leadsForCampaign(dispos: DispoRow[], campaign: string): DispoRow[] {
		return dispos.filter((d) => d.campaign_name === campaign && this.isLead(d.dispo));
	}

	// ── By Caller ────────────────────────────────────────────────────────────
	byCaller(dispos: DispoRow[], callerTimes: CallerTimeRow[]): CallerEffortRow[] {
		const top = this.topCampaignByCaller(dispos);
		const rows: CallerEffortRow[] = callerTimes.map((c) => {
			const dispos_ = this.n(c.totalDispos);
			const leads = this.n(c.green);
			return {
				name: c.name,
				dispos: dispos_,
				calls: this.n(c.totalCalls),
				hot: this.n(c.yellow),
				leads,
				talkMs: this.n(c.timeInCalls),
				prospectsPerLead: leads ? dispos_ / leads : 0,
				conv: dispos_ ? (leads / dispos_) * 100 : 0,
				topCampaign: top.get(c.name) ?? '—',
			};
		});
		// Rank by leads produced (production-first).
		return rows.sort((a, b) => b.leads - a.leads);
	}

	/**
	 * Fallback for when the caller-times endpoint fails: build By Caller from dispos ALONE.
	 * dispos / hot / leads / conv / topCampaign are real (from the dispo rows); Calls* and Talk*
	 * are unavailable (RingCentral/caller feed missing) and flagged for the UI to show "—".
	 */
	byCallerFromDispos(dispos: DispoRow[]): CallerEffortRow[] {
		const top = this.topCampaignByCaller(dispos);
		const groups = new Map<string, DispoRow[]>();
		for (const d of dispos) {
			const g = groups.get(d.caller_name) ?? [];
			g.push(d);
			groups.set(d.caller_name, g);
		}
		const rows: CallerEffortRow[] = [];
		groups.forEach((list, name) => {
			const dispos_ = list.length;
			const leads = list.filter((d) => this.isLead(d.dispo)).length;
			const hot = list.filter((d) => this.bucketOf(d.dispo) === 'yellow').length;
			rows.push({
				name,
				dispos: dispos_,
				calls: 0, // unavailable — caller-times endpoint failed
				hot,
				leads,
				talkMs: 0, // unavailable — caller-times endpoint failed
				prospectsPerLead: leads ? dispos_ / leads : 0,
				conv: dispos_ ? (leads / dispos_) * 100 : 0,
				topCampaign: top.get(name) ?? '—',
				callerMetricsUnavailable: true,
			});
		});
		return rows.sort((a, b) => b.leads - a.leads);
	}

	/** Green (lead) dispos a caller secured — the drill list. */
	leadsForCaller(dispos: DispoRow[], caller: string): DispoRow[] {
		return dispos.filter((d) => d.caller_name === caller && this.isLead(d.dispo));
	}

	/**
	 * Dispos-to-secure: for a lead's dispo row, filter to its campaign (optionally its caller),
	 * order by dispo `Id` ASC, and COUNT non-lead dispos since the previous green dispo. Count
	 * rows — never subtract Ids (Id is a global auto-increment; gaps are other campaigns).
	 */
	disposToSecure(dispos: DispoRow[], lead: DispoRow, byCaller = false): number {
		let rows = dispos.filter((d) => d.campaign_name === lead.campaign_name);
		if (byCaller) rows = rows.filter((d) => d.caller_name === lead.caller_name);
		rows = rows.slice().sort((a, b) => Number(a.id) - Number(b.id));
		let counter = 0;
		for (const d of rows) {
			if (this.isLead(d.dispo)) {
				if (String(d.id) === String(lead.id)) return counter;
				counter = 0; // previous lead — reset
			} else {
				counter += 1;
			}
		}
		return counter;
	}

	// ── Helpers ──────────────────────────────────────────────────────────────
	/** The campaign where a caller produced the most leads. */
	private topCampaignByCaller(dispos: DispoRow[]): Map<string, string> {
		const byCaller = new Map<string, Map<string, number>>();
		for (const d of dispos) {
			if (!this.isLead(d.dispo)) continue;
			const camps = byCaller.get(d.caller_name) ?? new Map<string, number>();
			camps.set(d.campaign_name, (camps.get(d.campaign_name) ?? 0) + 1);
			byCaller.set(d.caller_name, camps);
		}
		const top = new Map<string, string>();
		byCaller.forEach((camps, caller) => {
			let best = '';
			let bestN = -1;
			camps.forEach((n, c) => {
				if (n > bestN) {
					bestN = n;
					best = c;
				}
			});
			top.set(caller, best);
		});
		return top;
	}

	/**
	 * Talk time per campaign is ESTIMATED: time-in-call is only known per caller, so each
	 * caller's talk is allocated across the campaigns they dispo'd, weighted by dispo count.
	 * Σ per-campaign talk = Σ per-caller talk, so it reconciles. Always shown with a `*`.
	 */
	private talkByCampaign(dispos: DispoRow[], callerTimes: CallerTimeRow[]): Map<string, number> {
		const talkByCaller = new Map<string, number>();
		for (const c of callerTimes) talkByCaller.set(c.name, this.n(c.timeInCalls));

		const perCaller = new Map<string, { total: number; camps: Map<string, number> }>();
		for (const d of dispos) {
			const e = perCaller.get(d.caller_name) ?? { total: 0, camps: new Map<string, number>() };
			e.total += 1;
			e.camps.set(d.campaign_name, (e.camps.get(d.campaign_name) ?? 0) + 1);
			perCaller.set(d.caller_name, e);
		}

		const out = new Map<string, number>();
		perCaller.forEach((e, caller) => {
			const talk = talkByCaller.get(caller) ?? 0;
			if (!e.total || !talk) return;
			e.camps.forEach((cnt, camp) => {
				out.set(camp, (out.get(camp) ?? 0) + talk * (cnt / e.total));
			});
		});
		return out;
	}

	/** Worked-per-lead efficiency → 0-100 score (lower workload per lead = higher score). */
	effortScore(workedPerLead: number): number {
		return Math.max(0, Math.min(100, Math.round(140 - workedPerLead * 1.3)));
	}

	private n(v: number | string): number {
		const x = parseInt(v as string, 10);
		return isNaN(x) ? 0 : x;
	}
}
